import { useLayoutEffect } from 'react';
import * as Sentry from '@sentry/react';
import {
  AxiosError,
  AxiosHeaders,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from 'axios';

import { useLogout, useRefreshToken } from '@/features/auth';
import {
  clearImpersonation,
  getImpersonationToken,
} from '@/features/impersonation';
import {
  accountInstance,
  apiInstance,
  authInstance,
  channelManagementInstance,
  frontOfficeInstance,
  integrationsInstance,
  notificationInstance,
  subscriptionInstance,
} from '@/shared/api';
import { ApiError } from '@/shared/api/ApiError';
import { useAccountStore } from '@/shared/stores/accountStore';
import { dayjs } from '@/shared/utils/dayjs';

let globalRefreshPromise: Promise<void> | null = null;

function debounceRefresh(refresh: () => Promise<void>) {
  if (globalRefreshPromise) return globalRefreshPromise;
  globalRefreshPromise = refresh().finally(() => {
    globalRefreshPromise = null;
  });
  return globalRefreshPromise;
}

const defaultRequestInterceptor = async (
  config: InternalAxiosRequestConfig
) => {
  const { tempLocale } = useAccountStore.getState();
  const locale = tempLocale || 'ru';

  const newConfig = { ...config };
  newConfig.headers = new AxiosHeaders(config.headers);

  if (locale) {
    newConfig.headers.set('Accept-Language', locale);
  }

  newConfig.headers.set('X-Timezone', dayjs.tz.guess());

  // Impersonation: when sessionStorage holds an impersonation JWT, send it as
  // Bearer. Backend jwtMiddleware prefers Bearer over the partner's auth_token
  // cookie (see packages/auth/src/middleware.ts), so all subsequent requests
  // run as the client until the partner explicitly exits.
  const impToken = getImpersonationToken();
  if (impToken) {
    newConfig.headers.set('Authorization', `Bearer ${impToken}`);
  }

  return newConfig;
};

function applySentryContext(error: AxiosError) {
  if (error.response) {
    const { status, statusText, data, config } = error.response;

    if (status === 401) {
      return;
    }

    if (config?.url?.includes('/refresh') && status === 400) {
      return;
    }

    const requestInfo = {
      method: config?.method,
      url: config?.url,
    };

    const responseInfo = {
      status,
      statusText,
      hasBody: Boolean(data),
    };

    Sentry.withScope((scope) => {
      scope.setTag('http.status', status);
      scope.setContext('Request', requestInfo);
      scope.setContext('Response', responseInfo);

      Sentry.captureException(
        new Error(`HTTP Error ${status}: ${statusText}`)
      );
    });
  } else {
    Sentry.withScope((scope) => {
      scope.setTag('http.error_type', 'network');
      scope.setContext('Request', {
        method: error.config?.method,
        url: error.config?.url,
      });
      Sentry.captureException(new Error('Network Error'));
    });
  }
}

interface Make401HandlerOptions {
  /** Instance whose request triggered this handler — used to read its baseURL etc. */
  instance: AxiosInstance;
  /** Refresh access token. Debounced globally so concurrent 401s share one call. */
  refresh: () => Promise<void>;
  /** Called when refresh itself fails (refresh token invalid/expired). */
  onLogout: () => void;
  /**
   * Predicate that decides whether to attempt a refresh on this 401. Defaults to
   * "not /refresh, not /logout" — the same baseline guard the legacy interceptors
   * shared. Each instance can supply its own predicate to layer on /me checks etc.
   */
  shouldRefresh?: (config: AxiosRequestConfig | undefined) => boolean;
  /**
   * Instance to retry on. Defaults to `instance`. The legacy integrations
   * interceptor used authInstance for retries — that quirk is preserved by
   * passing this explicitly.
   */
  retryInstance?: AxiosInstance;
  /**
   * If false, don't return `{ data: { unauthorized: true }, status: 403 }` on 401/403.
   * Account interceptor was the only one that didn't do this — opt out by setting
   * false.
   */
  swallowUnauthorized?: boolean;
}

const defaultShouldRefresh = (config: AxiosRequestConfig | undefined): boolean =>
  !config?.url?.includes('/refresh') && !config?.url?.includes('/logout');

function make401Handler(opts: Make401HandlerOptions) {
  const swallow = opts.swallowUnauthorized !== false;
  const shouldRefresh = opts.shouldRefresh ?? defaultShouldRefresh;
  const retryInstance = opts.retryInstance ?? opts.instance;

  return async (error: AxiosError) => {
    applySentryContext(error);

    const apiError = ApiError.fromError(error);
    const originalRequest = error.config as
      | (InternalAxiosRequestConfig & { retry?: boolean })
      | undefined;

    if (
      error.response?.status === 401 &&
      !originalRequest?.retry &&
      shouldRefresh(originalRequest)
    ) {
      // Impersonation expired: don't refresh the partner's cookie session and
      // accidentally drop the partner back into the client cabinet under their
      // own credentials. Clear sessionStorage and reload — the partner lands
      // back in their own cabinet via the cookie.
      if (getImpersonationToken()) {
        clearImpersonation();
        window.location.reload();
        return Promise.reject(error);
      }

      originalRequest!.retry = true;
      try {
        await debounceRefresh(opts.refresh);
        return await retryInstance(originalRequest!);
      } catch (e) {
        opts.onLogout();
      }
    }

    if (
      swallow &&
      (error.response?.status === 403 || error.response?.status === 401)
    ) {
      return { data: { unauthorized: true }, status: 403 };
    }

    throw apiError;
  };
}

export function useApplyAppInterceptor() {
  const { refresh } = useRefreshToken();
  const { onLogout } = useLogout();

  useLayoutEffect(() => {
    // /me check + path guard: auth/account refresh only kicks in when calling /me
    // and not already on /auth pages. Mirrors legacy behavior.
    const meAndNotAuthPath = (config: AxiosRequestConfig | undefined) =>
      defaultShouldRefresh(config) &&
      !window.location.pathname.includes('/auth') &&
      Boolean(config?.url?.includes('/me'));

    const meOnly = (config: AxiosRequestConfig | undefined) =>
      defaultShouldRefresh(config) && Boolean(config?.url?.includes('/me'));

    const wire = (
      instance: AxiosInstance,
      handlerOpts: Omit<Make401HandlerOptions, 'instance' | 'refresh' | 'onLogout'>
    ) => {
      instance.interceptors.request.use(defaultRequestInterceptor);
      instance.interceptors.response.use(
        (response) => response,
        make401Handler({ instance, refresh, onLogout, ...handlerOpts })
      );
    };

    // Auth forms (sign-in, verify-code, forgot/reset password) must receive
    // real API errors to render inline form messages. Swallowing 401/403 here
    // hides invalid credentials from the user.
    wire(authInstance, {
      shouldRefresh: meAndNotAuthPath,
      swallowUnauthorized: false,
    });
    // account: same predicate as auth, but does NOT swallow 401/403 — it lets
    // the original axios error bubble to react-query.
    wire(accountInstance, {
      shouldRefresh: meAndNotAuthPath,
      swallowUnauthorized: false,
    });
    wire(apiInstance, {});
    wire(notificationInstance, {});
    // integrations: legacy quirk — retries via authInstance, not the integrations
    // instance itself. Preserved verbatim. /me-only refresh.
    wire(integrationsInstance, {
      shouldRefresh: meOnly,
      retryInstance: authInstance,
    });
    wire(channelManagementInstance, {});
    wire(frontOfficeInstance, {});
    wire(subscriptionInstance, {});
    // eslint-disable-next-line
  }, []);
}
